'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeColor {
  name: string;
  value: string; // HSL values e.g. "142 71% 45%"
  hex: string;
  label: string;
}

export interface ThemePreset {
  name: string;
  bg: string;
  accent: string;
  mode: 'light' | 'dark';
  primaryHsl: string;
}

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  primaryColor: ThemeColor;
  setPrimaryColor: (color: ThemeColor) => void;
  selectedPreset: string;
  applyPreset: (preset: ThemePreset) => void;
  isDark: boolean;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const defaultColor: ThemeColor = {
  name: 'Green',
  value: '142 71% 45%',
  hex: '#22c55e',
  label: 'Green',
};

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  setMode: () => {},
  primaryColor: defaultColor,
  setPrimaryColor: () => {},
  selectedPreset: 'Classic Light',
  applyPreset: () => {},
  isDark: false,
});

// ─── CSS Variable Helpers ─────────────────────────────────────────────────────

function hexToHsl(hex: string): string {
  let r = 0, g = 0, b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16) / 255;
    g = parseInt(hex.slice(3, 5), 16) / 255;
    b = parseInt(hex.slice(5, 7), 16) / 255;
  }
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyLightVars(root: HTMLElement, primaryHsl: string) {
  root.style.setProperty('--background', '0 0% 97%');
  root.style.setProperty('--foreground', '240 10% 10%');
  root.style.setProperty('--card', '0 0% 100%');
  root.style.setProperty('--card-foreground', '240 10% 10%');
  root.style.setProperty('--border', '240 6% 90%');
  root.style.setProperty('--input', '240 6% 90%');
  root.style.setProperty('--secondary', '240 5% 96%');
  root.style.setProperty('--secondary-foreground', '240 10% 20%');
  root.style.setProperty('--muted', '240 5% 95%');
  root.style.setProperty('--muted-foreground', '240 4% 46%');
  root.style.setProperty('--primary', primaryHsl);
  root.style.setProperty('--primary-foreground', '0 0% 100%');
}

function applyDarkVars(root: HTMLElement, primaryHsl: string) {
  root.style.setProperty('--background', '222 47% 8%');
  root.style.setProperty('--foreground', '210 40% 95%');
  root.style.setProperty('--card', '222 47% 11%');
  root.style.setProperty('--card-foreground', '210 40% 95%');
  root.style.setProperty('--border', '217 33% 20%');
  root.style.setProperty('--input', '217 33% 20%');
  root.style.setProperty('--secondary', '217 33% 15%');
  root.style.setProperty('--secondary-foreground', '210 40% 85%');
  root.style.setProperty('--muted', '217 33% 14%');
  root.style.setProperty('--muted-foreground', '215 20% 55%');
  root.style.setProperty('--primary', primaryHsl);
  root.style.setProperty('--primary-foreground', '0 0% 100%');
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>('light');
  const [primaryColor, setPrimaryColorState] = useState<ThemeColor>(defaultColor);
  const [selectedPreset, setSelectedPreset] = useState('Classic Light');
  const [systemDark, setSystemDark] = useState(false);

  // Detect system preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemDark(mq.matches);
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Load persisted settings
  useEffect(() => {
    try {
      const saved = localStorage.getItem('sf_theme');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.mode) setModeState(parsed.mode);
        if (parsed.primaryColor) setPrimaryColorState(parsed.primaryColor);
        if (parsed.selectedPreset) setSelectedPreset(parsed.selectedPreset);
      }
    } catch {}
  }, []);

  const isDark = mode === 'dark' || (mode === 'system' && systemDark);

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    const hsl = primaryColor.value || hexToHsl(primaryColor.hex);

    if (isDark) {
      root.classList.add('dark');
      applyDarkVars(root, hsl);
    } else {
      root.classList.remove('dark');
      applyLightVars(root, hsl);
    }
  }, [isDark, primaryColor]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    persist({ mode: newMode });
  }, []);

  const setPrimaryColor = useCallback((color: ThemeColor) => {
    setPrimaryColorState(color);
    persist({ primaryColor: color });
  }, []);

  const applyPreset = useCallback((preset: ThemePreset) => {
    const newMode: ThemeMode = preset.mode;
    const hsl = preset.primaryHsl;
    const hex = preset.accent;
    const color: ThemeColor = { name: 'Custom', value: hsl, hex, label: preset.name };

    setModeState(newMode);
    setPrimaryColorState(color);
    setSelectedPreset(preset.name);
    persist({ mode: newMode, primaryColor: color, selectedPreset: preset.name });
  }, []);

  function persist(partial: Partial<{ mode: ThemeMode; primaryColor: ThemeColor; selectedPreset: string }>) {
    try {
      const existing = JSON.parse(localStorage.getItem('sf_theme') || '{}');
      localStorage.setItem('sf_theme', JSON.stringify({ ...existing, ...partial }));
    } catch {}
  }

  return (
    <ThemeContext.Provider data-component-id="src/context/ThemeContext.tsx:183:4" data-component-path="src/context/ThemeContext.tsx" data-component-line="183" data-component-end-line="185" data-component-file="ThemeContext.tsx" data-component-name="ThemeContext.Provider" data-component-content="%7B%22elementName%22%3A%22ThemeContext.Provider%22%7D" value={{ mode, setMode, primaryColor, setPrimaryColor, selectedPreset, applyPreset, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
